import type { FC } from 'react';
import React, { memo, useMemo } from 'react';
import { Box, Flex } from '@withjoy/joykit';
import { SsrLazyScreen } from '@apps/guest/packages/layout-engine/components/SsrLazyScreen';
import { useGetFilledPhotos } from '../../hooks/useGetFilledPhotos';
import { MarqueeContainer } from './PhotoMarquee.styles';
import type { PhotoMarqueeProps } from './PhotoMarquee.types';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { OptimizedEventGuestSitePhoto } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto';
import { buildLowResUrl } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto/renditions';
import { MAX_ASPECT_RATIO, PHOTO_HEIGHT } from './PhotoMarquee.constants';
import { MarqueePhoto } from './MarqueePhoto';
import { findAspectRatioFromPercentCrop } from '@shared/utils/findAspectRatioFromPercentCrop';

const PHOTO_COUNT_TO_SHOW = 6;

export const PhotoMarquee: FC<PhotoMarqueeProps> = memo(props => {
  const { photos, showSkeleton, showPlaceholder, isPageWidth = false } = props;

  const { filledPhotos } = useGetFilledPhotos(photos, PHOTO_COUNT_TO_SHOW, showPlaceholder && photos.length === 0);
  const marqueePhotos = useMemo(() => [...filledPhotos, ...filledPhotos], [filledPhotos]);

  const [containerDimensions, containerRef] = useSizeObserver();

  if (!showPlaceholder && photos.length === 0) {
    return null;
  }

  return (
    <Flex
      width={isPageWidth ? '100%' : '100vw'}
      position="relative"
      left={isPageWidth ? '0' : 'calc(-50vw + 50%)'}
      overflow="hidden"
      // Height = own width / 12:7, from CSS alone: the page-width variant used to fall back to
      // 100vw/AR until a ResizeObserver measured the column, then collapse ~30% (measured CLS
      // 0.13). The viewport width is a CSS fact — aspect-ratio expresses it with no measurement.
      aspectRatio={`${MAX_ASPECT_RATIO}`}
      maxHeight={PHOTO_HEIGHT}
    >
      <MarqueeContainer
        flexDirection="row"
        flexBasis="100%"
        gap={1}
        height="100%"
        ref={containerRef}
      >
        {marqueePhotos.map((photo, index) => {
          // Same AR MarqueePhoto renders with; placeholder fills have no dimensions → 12:7.
          const rawAspectRatio = photo.cropInfo ? findAspectRatioFromPercentCrop(photo.cropInfo, photo.width, photo.height) : photo.width / photo.height;
          const tileAspectRatio = Number.isFinite(rawAspectRatio) && rawAspectRatio > 0 ? rawAspectRatio : MAX_ASPECT_RATIO;
          return (
            <SsrLazyScreen
              key={`${photo.id}-${index}`}
              src={buildLowResUrl(photo.photoUrl)}
              skeletonGroupOverrides={{
                Root: { props: { height: '100%', aspectRatio: `${tileAspectRatio}`, flexShrink: 0 } },
                PlaceholderRoot: { props: { height: '100%', aspectRatio: `${tileAspectRatio}`, flexShrink: 0 } },
              }}
              width="auto"
              height="100%"
              aspectRatio={`${tileAspectRatio}`}
              showSkeleton={showSkeleton}
            >
              {() =>
                photo.photoUrl ? (
                  <MarqueePhoto
                    photo={photo}
                    index={index}
                    containerDimensions={containerDimensions}
                    photoRenderer={OptimizedEventGuestSitePhoto}
                  />
                ) : (
                  <Box
                    width="100%"
                    height="100%"
                    aspectRatio={`${MAX_ASPECT_RATIO}`}
                    backgroundColor="lightgray"
                    key={photo.id}
                  />
                )
              }
            </SsrLazyScreen>
          );
        })}
      </MarqueeContainer>
    </Flex>
  );
});

PhotoMarquee.displayName = 'PhotoMarquee';
